import { authService } from './auth';

// Use proxy in development, environment-specific URL in production
const API_BASE_URL = import.meta.env.DEV 
  ? '' // Use proxy in development
  : import.meta.env.VITE_API_BASE_URL || 'https://chime-api-staging.braveforest-40c1a64b.eastus.azurecontainerapps.io';

export interface Post {
  id: number;
  title: string;
  description: string;
  state: string;
  anonymous: boolean;
  createdAt: string;
  updatedAt: string;
  upvoteCount: number;
  commentCount: number;
  upvotedByUser: boolean;
  viewedByUser: boolean;
  savedByUser: boolean;
  commentedByUser: boolean;
  owner: {
    id: number;
    username: string;
    name: string;
    profilePicture: string | null;
    country: string;
  };
  tags: Array<{
    id: number;
    text: string;
  }>;
  media: Array<{
    url: string;
    type: string;
  }>;
  poll?: {
    id: number;
    question: string | null;
    options: Array<{
      id: number;
      text: string;
    }>;
    results?: {
      totalVotes: number;
      votes: Array<{
        optionId: number;
        count: number;
      }>;
      votedForOptionId: number | null;
    };
  };
}

export interface Comment {
  id: number;
  comment: string;
  commenter: {
    id: number;
    username: string;
    name: string;
    country: string;
    profilePicture: string;
  };
  commenterId: number;
  parentId: number | null;
  createdAt: string;
  upvoteCount: number;
  upvotedByUser: boolean;
  mentions: Array<{
    userId: number;
    index: number;
  }>;
  replies: Comment[];
  replyCount: number;
  media?: {
    images?: string[];
    videos?: string[];
  };
}

export interface CreatePostData {
  title: string;
  description: string;
  tags?: Array<{ text: string }>;
  state: string;
  images?: File[];
  video?: File;
  pdfDocument?: File;
  poll?: {
    question: string;
    options: Array<{ position: number; text: string }>;
  };
}

export interface FeedParams {
  page?: number;
  limit?: number;
}

export interface ServerNotification {
  id: number;
  content: string;
  type: 'postUpvote' | 'pollVote' | 'comment';
  seen: boolean;
  createdAt: string;
  updatedAt: string;
  redirectId: number;
  sender: {
    id: number;
    username: string;
    profilePicture: string | null;
  };
}

class ApiService {
  async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    
    // Don't set Content-Type for FormData - browser will set it automatically with boundary
    const isFormData = options.body instanceof FormData;
    const authHeaders = authService.getAuthHeaders(!isFormData);
    const headers: Record<string, string> = {
      ...authHeaders,
      ...(options.headers as Record<string, string> || {}),
    };
    
    // Debug auth headers
    console.log('🔍 Request headers:', {
      hasAuth: !!authHeaders.Authorization,
      authLength: authHeaders.Authorization?.length,
      endpoint,
      method: options.method,
      finalHeaders: Object.keys(headers),
      authInFinal: !!headers.Authorization
    });
    
    const config: RequestInit = {
      headers,
      mode: 'cors',
      ...options,
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        let errorText = '';
        let errorMessage = '';

        try {
          errorText = await response.text();
          console.error('API Error Response:', errorText);

          // Try to parse error JSON for better error messages
          try {
            const errorJson = JSON.parse(errorText);
            if (errorJson.violations && errorJson.violations.length > 0) {
              const violationList = errorJson.violations.map((v: any) => `${v.file}: ${v.description}`).join('; ');
              errorMessage = `${errorJson.error}. Details: ${violationList}`;
            } else {
              errorMessage = errorJson.message || errorJson.error || errorText;
            }
          } catch {
            errorMessage = errorText;
          }
        } catch {
          errorMessage = `Request failed with status ${response.status}`;
        }

        // Handle authentication errors (401/403)
        if (response.status === 401 || response.status === 403) {
          console.warn('🚨 Authentication failed - token expired or invalid');
          authService.handleAuthError();
          throw new Error('Your session has expired. Please log in again.');
        }

        // Handle specific error codes with user-friendly messages
        if (response.status === 404) {
          throw new Error('The requested resource was not found.');
        }

        if (response.status === 400) {
          throw new Error(errorMessage || 'Invalid request. Please check your input.');
        }

        if (response.status >= 500) {
          throw new Error('Server error. Please try again later.');
        }

        throw new Error(errorMessage || `Request failed with status ${response.status}`);
      }

      const text = await response.text();
      if (!text || response.status === 204) {
        // Handle 204 No Content response (successful but no body)
        console.log('✅ Request successful (204 No Content)');
        return { success: true } as T;
      }
      
      try {
        return JSON.parse(text);
      } catch (parseError) {
        console.error('Failed to parse JSON response:', text);
        throw new Error('Invalid response from server. Please try again.');
      }
    } catch (error: any) {
      console.error('API request failed:', error);

      // Handle network errors
      if (error.name === 'TypeError' && (error.message === 'Failed to fetch' || error.message === 'Network request failed')) {
        throw new Error('Network error. Please check your internet connection.');
      }

      // Re-throw the error with original message if it's already formatted
      throw error;
    }
  }

  async getPosts(params: FeedParams = {}): Promise<Post[]> {
    const queryParams = new URLSearchParams();
    // Page and limit are required by the API
    queryParams.append('page', (params.page || 0).toString());
    queryParams.append('limit', (params.limit || 50).toString()); // Increase default limit to get more posts
    
    const query = queryParams.toString();
    const endpoint = `/api/v1/post/get_posts?${query}`;
    
    console.log('🔍 Fetching posts from:', endpoint);
    const posts = await this.request<Post[]>(endpoint);
    
    console.log('📊 Fetched posts:', posts.length, 'posts');
    if (posts.length > 0) {
      console.log('📅 Post info:');
      posts.forEach((post, index) => {
        console.log(`  ${index + 1}. Post ID ${post.id} - "${post.title}"`);
        console.log(`     Created: ${new Date(post.createdAt).toLocaleDateString()}`);
        console.log(`     Tags: ${post.tags?.length || 0} tags`, post.tags);
        console.log(`     Media: ${post.media?.length || 0} items`);
        if (post.media && post.media.length > 0) {
          post.media.forEach((media, idx) => {
            console.log(`       ${idx + 1}. ${(media as any).url || (media as any).URL || 'NO_URL'} (${media.type})`);
          });
        }
      });
      
      const sortedPosts = posts.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      console.log('✅ After sorting - Latest post:', sortedPosts[0]?.createdAt, 'Oldest post:', sortedPosts[sortedPosts.length - 1]?.createdAt);
      return sortedPosts;
    }
    
    return posts;
  }

  async getUserPosts(params: FeedParams = {}): Promise<Post[]> {
    const queryParams = new URLSearchParams();
    queryParams.append('page', (params.page || 0).toString());
    queryParams.append('limit', (params.limit || 50).toString());
    
    const query = queryParams.toString();
    const endpoint = `/api/v1/posts/my_posts?${query}`;
    
    console.log('🔍 Fetching user posts from:', endpoint);
    const posts = await this.request<Post[]>(endpoint);
    
    console.log('👤 Fetched user posts:', posts.length, 'posts');
    return posts.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }

  async getSavedPosts(params: FeedParams = {}): Promise<Post[]> {
    const queryParams = new URLSearchParams();
    queryParams.append('page', (params.page || 0).toString());
    queryParams.append('limit', (params.limit || 50).toString());
    
    const query = queryParams.toString();
    const endpoint = `/api/v1/post/saved_posts?${query}`;
    
    console.log('🔍 Fetching saved posts from:', endpoint);
    const posts = await this.request<Post[]>(endpoint);
    
    console.log('💾 Fetched saved posts:', posts.length, 'posts');
    return posts.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }

  async searchPosts(query: string, params: FeedParams = {}): Promise<Post[]> {
    const queryParams = new URLSearchParams();
    queryParams.append('input', query);
    queryParams.append('page', (params.page || 0).toString());
    queryParams.append('limit', (params.limit || 20).toString());
    
    const queryString = queryParams.toString();
    const endpoint = `/api/v1/post/search?${queryString}`;
    
    console.log('🔍 Searching posts with query:', query, 'from:', endpoint);
    const posts = await this.request<Post[]>(endpoint);
    
    console.log('🔎 Found search results:', posts.length, 'posts');
    return posts.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }

  async createPost(data: CreatePostData): Promise<Post> {
    // Check if we have files to upload
    const hasFiles = (data.images && data.images.length > 0) || data.video || data.pdfDocument;

    if (hasFiles) {
      // Two-step process: Upload media first, then create post with media URLs
      console.log('🖼️ Creating post with file upload using two-step process...');

      const mediaUrls: string[] = [];
      const mediaTypes: string[] = [];

      // Step 1: Upload media files
      if (data.images && data.images.length > 0) {
        console.log(`📤 Uploading ${data.images.length} images...`);
        for (const image of data.images) {
          const imageUrl = await this.uploadMedia(image);
          mediaUrls.push(imageUrl);
          // Detect actual type from file MIME type instead of assuming 'image'
          const detectedType = image.type.startsWith('video/') ? 'video' : image.type === 'application/pdf' ? 'pdf' : 'image';
          mediaTypes.push(detectedType);
          console.log(`✅ Media uploaded (${detectedType}):`, imageUrl);
        }
      }

      if (data.video) {
        console.log('📤 Uploading video...');
        const videoUrl = await this.uploadMedia(data.video);
        mediaUrls.push(videoUrl);
        mediaTypes.push('video');
        console.log('✅ Video uploaded:', videoUrl);
      }

      if (data.pdfDocument) {
        console.log('📤 Uploading PDF...');
        const pdfUrl = await this.uploadMedia(data.pdfDocument);
        mediaUrls.push(pdfUrl);
        mediaTypes.push('pdf');
        console.log('✅ PDF uploaded:', pdfUrl);
      }

      // Step 2: Create post with media URLs using JSON
      console.log('📝 Creating post with media URLs...');
      const postPayload = {
        title: data.title,
        description: data.description,
        state: data.state === 'published' ? 'public' : data.state,
        tags: data.tags?.map(tag => ({
          text: tag.text.toLowerCase()
        })) || [],
        media: mediaUrls.map((url, index) => ({
          URL: url, // Server expects uppercase "URL" for sending
          type: mediaTypes[index] // Use the tracked media type
        }))
      };

      // Add poll if provided
      if (data.poll) {
        (postPayload as any).poll = data.poll;
      }

      console.log('📦 Post payload with media:', postPayload);

      try {
        // Ensure auth headers are explicitly included
        const authHeaders = authService.getAuthHeaders();
        const result = await this.request<Post>('/api/v1/post/upload', {
          method: 'POST',
          body: JSON.stringify(postPayload),
          headers: {
            'Content-Type': 'application/json',
            ...authHeaders, // Include ALL auth headers (Authorization, X-Request-ID, X-Impersonate-User-ID)
          },
        });
        console.log('✅ Post with media created successfully!');
        return result;
      } catch (error) {
        console.error('❌ Post creation with media failed:', error);
        throw error;
      }
    } else {
      // Use JSON for text-only posts (more reliable)
      console.log('📝 Creating text-only post with JSON...');
      
      const postPayload = {
        title: data.title,
        description: data.description, // Staging expects "description", not "body"
        state: data.state === 'published' ? 'public' : data.state, // Convert published -> public
        tags: data.tags?.map(tag => ({ 
          text: tag.text.toLowerCase() // Convert to lowercase (shoulder, hip, knee, ankle)
        })) || [],
      };

      // Add poll if provided
      if (data.poll) {
        (postPayload as any).poll = data.poll;
      }

      console.log('🚀 Creating post with JSON payload:', postPayload);

      try {
        // Ensure auth headers are explicitly included
        const authHeaders = authService.getAuthHeaders();
        const result = await this.request<Post>('/api/v1/post/upload', {
          method: 'POST',
          body: JSON.stringify(postPayload),
          headers: {
            'Content-Type': 'application/json',
            ...authHeaders, // Include ALL auth headers (Authorization, X-Request-ID, X-Impersonate-User-ID)
          },
        });
        console.log('✅ Text post created successfully!');
        return result;
      } catch (error) {
        console.error('❌ Text post creation failed:', error);
        throw error;
      }
    }
  }

  async updatePost(postId: number, data: CreatePostData): Promise<void> {
    console.log('📝 Updating draft post:', postId);

    const mediaUrls: string[] = [];
    const mediaTypes: string[] = [];

    // Upload new media files if any
    if (data.images && data.images.length > 0) {
      for (const image of data.images) {
        const url = await this.uploadMedia(image);
        mediaUrls.push(url);
        const detectedType = image.type.startsWith('video/') ? 'video' : image.type === 'application/pdf' ? 'pdf' : 'image';
        mediaTypes.push(detectedType);
      }
    }

    if (data.video) {
      const videoUrl = await this.uploadMedia(data.video);
      mediaUrls.push(videoUrl);
      mediaTypes.push('video');
    }

    if (data.pdfDocument) {
      const pdfUrl = await this.uploadMedia(data.pdfDocument);
      mediaUrls.push(pdfUrl);
      mediaTypes.push('pdf');
    }

    const postPayload: any = {
      id: postId,
      title: data.title,
      description: data.description,
      state: data.state === 'published' ? 'public' : data.state,
      tags: data.tags?.map(tag => ({
        text: tag.text.toLowerCase()
      })) || [],
      media: mediaUrls.map((url, index) => ({
        URL: url,
        type: mediaTypes[index]
      }))
    };

    if (data.poll) {
      postPayload.poll = data.poll;
    }

    const authHeaders = authService.getAuthHeaders();
    await this.request<void>('/api/v1/post/update', {
      method: 'POST',
      body: JSON.stringify(postPayload),
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
      },
    });
    console.log('✅ Post updated successfully');
  }

  async uploadMedia(file: File): Promise<string> {
    console.log('📤 Uploading media file:', file.name, file.type);
    
    const formData = new FormData();
    formData.append('media', file);
    
    try {
      // Updated to handle new backend response format with Azure Storage support
      const response = await this.request<{
        // Single file response
        url?: string;
        file?: {
          URL: string;
          storage: 'azure' | 'local';
          originalName: string;
          size: number;
          contentType: string;
        };
        // Multiple files response
        files?: Array<{
          URL: string;
          storage: 'azure' | 'local';
          originalName: string;
          size: number;
          contentType: string;
        }>;
        count?: number;
        storageType?: 'azure' | 'local';
      } | Array<{URL: string}>>('/api/v1/media/upload', {
        method: 'POST',
        body: formData,
        // Don't set Content-Type - browser will set it with boundary for FormData
      });
      
      // Handle single file response format (most common)
      if (response && 'url' in response && response.url) {
        console.log(`✅ Media uploaded successfully to ${response.storageType}:`, response.url);
        return response.url;
      }
      // Handle multiple files response format
      else if (response && 'files' in response && response.files && response.files.length > 0) {
        const uploadedFile = response.files[0];
        console.log(`✅ Media uploaded successfully to ${response.storageType}:`, uploadedFile.URL);
        console.log(`📊 File details: ${uploadedFile.originalName} (${uploadedFile.size} bytes)`);
        return uploadedFile.URL;
      }
      // Handle legacy response format for backward compatibility
      else if (Array.isArray(response) && response.length > 0 && response[0].URL) {
        console.log('🔄 Using legacy response format');
        console.log('✅ Media uploaded successfully:', response[0].URL);
        return response[0].URL;
      } else {
        throw new Error('No URL returned from media upload');
      }
    } catch (error) {
      console.error('❌ Media upload failed:', error);
      throw new Error(`Media upload failed: ${(error as Error).message}`);
    }
  }

  async likePost(postId: number): Promise<void> {
    console.log('👍 Liking post with explicit auth headers...');
    
    const authHeaders = authService.getAuthHeaders();
    await this.request(`/api/v1/post/upvote`, {
      method: 'POST',
      body: JSON.stringify({ postId }),
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authHeaders.Authorization,
        'X-Request-ID': authHeaders['X-Request-ID'],
      },
    });
    
    console.log('✅ Post liked successfully!');
  }

  async unlikePost(postId: number): Promise<void> {
    console.log('👎 Unliking post with explicit auth headers...');
    
    const authHeaders = authService.getAuthHeaders();
    await this.request(`/api/v1/post/upvote`, {
      method: 'POST',
      body: JSON.stringify({ postId }),
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authHeaders.Authorization,
        'X-Request-ID': authHeaders['X-Request-ID'],
      },
    });
    
    console.log('✅ Post unliked successfully!');
  }

  async savePost(postId: number): Promise<void> {
    console.log('💾 Saving post with explicit auth headers...');
    
    const authHeaders = authService.getAuthHeaders();
    await this.request(`/api/v1/post/save`, {
      method: 'POST',
      body: JSON.stringify({ postId }),
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authHeaders.Authorization,
        'X-Request-ID': authHeaders['X-Request-ID'],
      },
    });
    
    console.log('✅ Post saved successfully!');
  }

  async unsavePost(postId: number): Promise<void> {
    console.log('🗑️ Unsaving post with explicit auth headers...');
    
    const authHeaders = authService.getAuthHeaders();
    await this.request(`/api/v1/post/save`, {
      method: 'POST',
      body: JSON.stringify({ postId }),
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authHeaders.Authorization,
        'X-Request-ID': authHeaders['X-Request-ID'],
      },
    });
    
    console.log('✅ Post unsaved successfully!');
  }

  async getComments(postId: number, page: number = 0, limit: number = 10): Promise<Comment[]> {
    const comments = await this.request<any[]>(`/api/v1/post/get_comments?postId=${postId}&page=${page}&limit=${limit}`);
    
    // Transform backend media format to frontend format
    return comments.map(comment => ({
      ...comment,
      media: comment.media && comment.media.length > 0 ? {
        images: comment.media.filter((m: any) => m.type === 'image').map((m: any) => m.url),
        videos: comment.media.filter((m: any) => m.type === 'video').map((m: any) => m.url)
      } : undefined
    }));
  }

  async createComment(postId: number, comment: string, media?: { images?: File[], videos?: File[] }, parentId?: number): Promise<void> {
    console.log('💬 Creating comment with explicit auth headers...');

    // Ensure auth headers are explicitly included
    const authHeaders = authService.getAuthHeaders();
    
    // If media is provided, try to upload files first then create comment with URLs
    if (media && (media.images?.length || media.videos?.length)) {
      console.log('📤 Creating comment with media using two-step process...');
      console.log('⚠️ Note: Comment media support is pending server-side implementation');
      
      try {
        const mediaUrls: string[] = [];
        
        // Step 1: Upload media files
        if (media.images) {
          for (const image of media.images) {
            console.log('📤 Uploading image:', image.name);
            const imageUrl = await this.uploadMedia(image);
            mediaUrls.push(imageUrl);
            console.log('✅ Image uploaded:', imageUrl);
          }
        }
        
        if (media.videos) {
          for (const video of media.videos) {
            console.log('📤 Uploading video:', video.name);
            const videoUrl = await this.uploadMedia(video);
            mediaUrls.push(videoUrl);
            console.log('✅ Video uploaded:', videoUrl);
          }
        }
        
        // Step 2: Try to create comment with media URLs using JSON
        console.log('💬 Attempting to create comment with media URLs...');
        
        const imageUrls = mediaUrls.slice(0, media.images?.length || 0);
        const videoUrls = mediaUrls.slice(media.images?.length || 0);
        
        // Transform frontend format to backend format
        const mediaArray = [
          ...imageUrls.map(url => ({ type: 'image', url })),
          ...videoUrls.map(url => ({ type: 'video', url }))
        ];
        
        const commentPayload = {
          postId,
          comment,
          media: mediaArray.length > 0 ? mediaArray : undefined,
          parentId: parentId || undefined
        };
        
        console.log('📋 Comment payload with media:', commentPayload);
        
        await this.request(`/api/v1/post/add_comment`, {
          method: 'POST',
          body: JSON.stringify(commentPayload),
          headers: {
            'Content-Type': 'application/json',
            'Authorization': authHeaders.Authorization,
            'X-Request-ID': authHeaders['X-Request-ID'],
          },
        });
      } catch (error) {
        console.error('Failed to create comment with media:', error);
        throw error; // Re-throw the actual error
      }
    } else {
      // No media, send as regular JSON
      await this.request(`/api/v1/post/add_comment`, {
        method: 'POST',
        body: JSON.stringify({ postId, comment, parentId: parentId || undefined }),
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authHeaders.Authorization,
          'X-Request-ID': authHeaders['X-Request-ID'],
        },
      });
    }
    
    console.log('✅ Comment created successfully!');
  }

  async sharePost(postId: number): Promise<void> {
    await this.request(`/api/v1/post/${postId}/share`, {
      method: 'POST',
    });
  }

  async deletePost(postId: number): Promise<void> {
    console.log('🗑️ Deleting post with explicit auth headers...');
    
    const authHeaders = authService.getAuthHeaders();
    await this.request(`/api/v1/post/delete`, {
      method: 'POST',
      headers: {
        'Authorization': authHeaders.Authorization,
        'X-Request-ID': authHeaders['X-Request-ID'],
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        postId: postId
      })
    });
    
    console.log('✅ Post deleted successfully!');
  }

  async votePoll(pollId: number, optionId: number): Promise<any> {
    console.log('🗳️ Voting on poll with explicit auth headers...');
    
    const authHeaders = authService.getAuthHeaders();
    const response = await this.request(`/api/v1/post/poll_vote`, {
      method: 'POST',
      body: JSON.stringify({ pollId, pollOptionId: optionId }),
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authHeaders.Authorization,
        'X-Request-ID': authHeaders['X-Request-ID'],
      },
    });
    
    console.log('✅ Poll vote recorded successfully!');
    return response;
  }

  async getPollResults(pollId: number): Promise<any> {
    console.log('📊 Getting poll results...');
    
    const response = await this.request(`/api/v1/post/poll_results?pollId=${pollId}`, {
      method: 'GET',
    });
    
    console.log('✅ Poll results retrieved successfully!');
    return response;
  }

  async upvoteComment(commentId: number): Promise<void> {
    console.log('👍 Upvoting comment with explicit auth headers...');
    
    try {
      const authHeaders = authService.getAuthHeaders();
      
      // First try with the modified schema
      console.log('Attempting upvote with allowSelfUpvote parameter');
      try {
        await this.request(`/api/v1/post/comment_upvote`, {
          method: 'POST',
          body: JSON.stringify({ 
            commentId,
            allowSelfUpvote: true // Explicitly allow upvoting own comments
          }),
          headers: {
            'Content-Type': 'application/json',
            'Authorization': authHeaders.Authorization,
            'X-Request-ID': authHeaders['X-Request-ID'],
          },
        });
        console.log('✅ Comment upvote toggled successfully!');
        return;
      } catch (firstError) {
        // If first attempt fails, try without the allowSelfUpvote parameter
        // (in case the backend hasn't been updated)
        console.log('First upvote attempt failed, trying without allowSelfUpvote', firstError);
        await this.request(`/api/v1/post/comment_upvote`, {
          method: 'POST',
          body: JSON.stringify({ commentId }),
          headers: {
            'Content-Type': 'application/json',
            'Authorization': authHeaders.Authorization,
            'X-Request-ID': authHeaders['X-Request-ID'],
          },
        });
        console.log('✅ Comment upvote toggled successfully (fallback)!');
      }
    } catch (error) {
      console.error('❌ Failed to upvote comment:', error);
      throw error;
    }
  }
  
  async deleteComment(commentId: number): Promise<void> {
    console.log('🗑️ Deleting comment with explicit auth headers...');
    
    try {
      const authHeaders = authService.getAuthHeaders();
      await this.request(`/api/v1/post/delete_comment`, {
        method: 'POST',
        body: JSON.stringify({ commentId }),
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authHeaders.Authorization,
          'X-Request-ID': authHeaders['X-Request-ID'],
        },
      });
      console.log('✅ Comment deleted successfully!');
    } catch (error) {
      console.error('❌ Failed to delete comment:', error);
      throw error;
    }
  }

  // Notification methods
  async getNotifications(page: number = 0, limit: number = 20): Promise<ServerNotification[]> {
    const queryParams = new URLSearchParams();
    queryParams.append('page', page.toString());
    queryParams.append('limit', limit.toString());
    
    const endpoint = `/api/v1/user/notifications?${queryParams.toString()}`;
    console.log('🔍 Fetching notifications from:', endpoint);
    
    const notifications = await this.request<ServerNotification[]>(endpoint);
    console.log('🔔 Fetched notifications:', notifications.length, 'notifications');
    
    return notifications;
  }

  // Notification settings methods (for future API integration)
  async getNotificationSettings(): Promise<any> {
    return this.request('/api/v1/user/notification-settings');
  }

  async updateNotificationSetting(setting: string, enabled: boolean): Promise<void> {
    await this.request('/api/v1/notification/update', {
      method: 'POST',
      body: JSON.stringify({ setting, enabled }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  async updateAllNotificationSettings(settings: Record<string, boolean>): Promise<void> {
    await this.request('/api/v1/user/notification-settings', {
      method: 'POST',
      body: JSON.stringify(settings),
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  async markNotificationAsSeen(notificationId: string): Promise<void> {
    await this.request(`/api/v1/user/notifications/${notificationId}/mark-seen`, {
      method: 'POST',
    });
  }

  async deleteMyAccount(): Promise<void> {
    console.log('🗑️ Deleting account...');

    const authHeaders = authService.getAuthHeaders();
    await this.request(`/api/v1/user/delete_my_account`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authHeaders.Authorization,
        'X-Request-ID': authHeaders['X-Request-ID'],
      },
    });

    console.log('✅ Account deleted successfully!');
  }

  // Helper method for DELETE requests
  async delete<T = void>(endpoint: string, options: Omit<RequestInit, 'method'> = {}): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'DELETE',
    });
  }
}

export const apiService = new ApiService();